import { opposite, sameTile, step } from "./direction";
import { DIRECTIONS, type CellKind, type Direction, type Tile } from "./types";

/**
 * The station deck plan.
 *
 * Topologically this is the classic Pac-Man maze -- four power pellets in the
 * corners, a central den with a one-way gate, flanking vertical trunks and a
 * wrap-around tunnel row -- because the ghost AI in `ghost.ts` is written
 * against that shape (scatter corners, the up-turn ban tiles, den exits).
 * Re-skinned as a space station: the walls render as hull modules and the
 * tunnel row as a docking umbilical.
 *
 * Legend:
 *   `#` hull        `.` Story Point   `o` 5h Quota Reset (power pellet)
 *   ` ` empty deck  `-` den gate      `_` den interior
 *   `P` player spawn (empty deck)     `T` tunnel deck (empty, wraps)
 */
export const DECK_PLAN: readonly string[] = [
  "############################",
  "#............##............#",
  "#.####.#####.##.#####.####.#",
  "#o####.#####.##.#####.####o#",
  "#.####.#####.##.#####.####.#",
  "#..........................#",
  "#.####.##.########.##.####.#",
  "#.####.##.########.##.####.#",
  "#......##....##....##......#",
  "######.##### ## #####.######",
  "######.##### ## #####.######",
  "######.##          ##.######",
  "######.## ###--### ##.######",
  "######.## #______# ##.######",
  "TTTTTT.   #______#   .TTTTTT",
  "######.## #______# ##.######",
  "######.## ######## ##.######",
  "######.##          ##.######",
  "######.## ######## ##.######",
  "######.## ######## ##.######",
  "#............##............#",
  "#.####.#####.##.#####.####.#",
  "#.####.#####.##.#####.####.#",
  "#o..##.......P .......##..o#",
  "###.##.##.########.##.##.###",
  "###.##.##.########.##.##.###",
  "#......##....##....##......#",
  "#.##########.##.##########.#",
  "#.##########.##.##########.#",
  "#..........................#",
  "############################",
];

const SYMBOLS: Readonly<Record<string, CellKind>> = {
  "#": "wall",
  ".": "storyPoint",
  o: "quotaReset",
  " ": "corridor",
  "-": "door",
  _: "den",
  P: "corridor",
  T: "corridor",
};

export interface MazeSpawns {
  readonly player: Tile;
  /** Where each ghost starts: Blinky sits on the gate, the rest wait inside. */
  readonly blinky: Tile;
  readonly pinky: Tile;
  readonly inky: Tile;
  readonly clyde: Tile;
  /** The deck tile directly above the gate; every ghost aims here to get out. */
  readonly denExit: Tile;
}

export class Maze {
  readonly width: number;
  readonly height: number;
  readonly spawns: MazeSpawns;
  /** Row index of the wrap-around tunnel, or -1 when the plan has none. */
  readonly tunnelRow: number;
  /**
   * Junctions where a ghost may not choose to turn up.
   *
   * The arcade forbids the upward turn at four specific tiles, which is what
   * makes certain loops safe for the player. Faithful ports keep it.
   */
  readonly noUpTurnTiles: readonly Tile[];
  readonly scatterTargets: Readonly<Record<"blinky" | "pinky" | "inky" | "clyde", Tile>>;

  private readonly cells: CellKind[][];

  constructor(plan: readonly string[] = DECK_PLAN) {
    const first = plan[0];
    if (first === undefined) throw new Error("deck plan is empty");
    this.height = plan.length;
    this.width = first.length;

    this.cells = plan.map((row, y) => {
      if (row.length !== this.width) {
        throw new Error(`deck plan row ${y} is ${row.length} wide, expected ${this.width}`);
      }
      return [...row].map((ch, x) => {
        const kind = SYMBOLS[ch];
        if (kind === undefined) throw new Error(`unknown deck symbol "${ch}" at ${x},${y}`);
        return kind;
      });
    });

    const playerMark = findSymbol(plan, "P");
    if (playerMark === null) throw new Error("deck plan has no player spawn 'P'");

    const gate = this.findAll("door");
    if (gate.length === 0) throw new Error("deck plan has no den gate '-'");
    // floor, not round: with a two-tile gate this lands on the left gate tile,
    // which is where the arcade's den exit and up-turn ban tiles are measured from.
    const gateX = Math.floor(gate.reduce((a, t) => a + t.x, 0) / gate.length);
    const gateY = gate[0]!.y;
    const den = this.findAll("den");
    const denY = Math.round(den.reduce((a, t) => a + t.y, 0) / den.length);

    this.spawns = {
      player: playerMark,
      denExit: { x: gateX, y: gateY - 1 },
      blinky: { x: gateX, y: gateY - 1 },
      pinky: { x: gateX, y: denY },
      inky: { x: gateX - 2, y: denY },
      clyde: { x: gateX + 2, y: denY },
    };

    this.tunnelRow = plan.findIndex((row) => row.startsWith("T"));

    this.noUpTurnTiles = [
      { x: gateX - 1, y: gateY - 1 },
      { x: gateX + 2, y: gateY - 1 },
      { x: gateX - 1, y: gateY + 11 },
      { x: gateX + 2, y: gateY + 11 },
    ].filter((t) => this.inBounds(t) && this.isOpen(t, false));

    this.scatterTargets = {
      pinky: { x: 2, y: -3 },
      blinky: { x: this.width - 3, y: -3 },
      clyde: { x: 0, y: this.height + 2 },
      inky: { x: this.width - 1, y: this.height + 2 },
    };
  }

  private findAll(kind: CellKind): Tile[] {
    const out: Tile[] = [];
    for (let y = 0; y < this.height; y += 1) {
      for (let x = 0; x < this.width; x += 1) {
        if (this.cells[y]![x] === kind) out.push({ x, y });
      }
    }
    return out;
  }

  inBounds(tile: Tile): boolean {
    return tile.x >= 0 && tile.x < this.width && tile.y >= 0 && tile.y < this.height;
  }

  /** Wraps x through the tunnel; y is never wrapped. */
  normalise(tile: Tile): Tile {
    if (tile.y !== this.tunnelRow) return tile;
    return { x: ((tile.x % this.width) + this.width) % this.width, y: tile.y };
  }

  kindAt(tile: Tile): CellKind {
    const t = this.normalise(tile);
    if (!this.inBounds(t)) return "wall";
    return this.cells[t.y]![t.x]!;
  }

  /**
   * Can an actor stand here?
   *
   * `canUseDen` distinguishes the two exceptions: the den gate and the den floor
   * are ghost-only. The player can never enter them.
   */
  isOpen(tile: Tile, canUseDen: boolean): boolean {
    const kind = this.kindAt(tile);
    if (kind === "wall") return false;
    if (kind === "door" || kind === "den") return canUseDen;
    return true;
  }

  /** Every direction an actor at `tile` could move into. */
  openDirections(tile: Tile, canUseDen: boolean): Direction[] {
    return DIRECTIONS.filter((dir) => this.isOpen(step(tile, dir), canUseDen));
  }

  /**
   * True where a ghost must re-decide: anything that is not a plain corridor
   * or a dead end. Corners count, since the ghost has to pick the new axis.
   */
  isDecisionTile(tile: Tile, canUseDen: boolean): boolean {
    return this.openDirections(tile, canUseDen).length > 2;
  }

  isNoUpTurnTile(tile: Tile): boolean {
    return this.noUpTurnTiles.some((t) => sameTile(t, tile));
  }

  /** Tiles a fresh level seeds with Story Points and 5h Quota Resets. */
  pelletTiles(): { storyPoints: Tile[]; quotaResets: Tile[] } {
    return { storyPoints: this.findAll("storyPoint"), quotaResets: this.findAll("quotaReset") };
  }

  /**
   * Every corridor tile reachable by the player from `from`.
   *
   * Used by the level validator: a Story Point the player cannot reach makes
   * the level unwinnable, which is a bug worth failing a test over.
   */
  reachable(from: Tile, canUseDen = false): Set<string> {
    const seen = new Set<string>([key(from)]);
    const queue: Tile[] = [from];
    while (queue.length > 0) {
      const cur = queue.shift()!;
      for (const dir of DIRECTIONS) {
        const next = this.normalise(step(cur, dir));
        if (!this.isOpen(next, canUseDen) || seen.has(key(next))) continue;
        seen.add(key(next));
        queue.push(next);
      }
    }
    return seen;
  }

  /** Shortest player-legal path length in tiles, or -1 when unreachable. */
  pathLength(from: Tile, to: Tile, canUseDen = false): number {
    if (sameTile(from, to)) return 0;
    const dist = new Map<string, number>([[key(from), 0]]);
    const queue: Tile[] = [from];
    while (queue.length > 0) {
      const cur = queue.shift()!;
      const d = dist.get(key(cur))!;
      for (const dir of DIRECTIONS) {
        const next = this.normalise(step(cur, dir));
        if (!this.isOpen(next, canUseDen) || dist.has(key(next))) continue;
        if (sameTile(next, to)) return d + 1;
        dist.set(key(next), d + 1);
        queue.push(next);
      }
    }
    return -1;
  }

  /** The first legal step from `from` towards `to`, by breadth-first search. */
  stepTowards(from: Tile, to: Tile, canUseDen: boolean, banned?: Direction): Direction | null {
    for (const dir of DIRECTIONS) {
      if (banned !== undefined && dir === opposite(banned)) continue;
      const next = this.normalise(step(from, dir));
      if (this.isOpen(next, canUseDen) && sameTile(next, to)) return dir;
    }
    let best: Direction | null = null;
    let bestLen = Number.POSITIVE_INFINITY;
    for (const dir of DIRECTIONS) {
      if (banned !== undefined && dir === opposite(banned)) continue;
      const next = this.normalise(step(from, dir));
      if (!this.isOpen(next, canUseDen)) continue;
      const len = this.pathLength(next, to, canUseDen);
      if (len >= 0 && len < bestLen) {
        bestLen = len;
        best = dir;
      }
    }
    return best;
  }
}

const findSymbol = (plan: readonly string[], symbol: string): Tile | null => {
  for (let y = 0; y < plan.length; y += 1) {
    const x = plan[y]!.indexOf(symbol);
    if (x >= 0) return { x, y };
  }
  return null;
};

export const key = (tile: Tile): string => `${tile.x},${tile.y}`;
